// ConfirmDialogProvider.tsx
// ---------------------------------------------------------------------------
// A single, app-wide confirmation dialog for Material UI apps.
//
// Instead of rendering a <Dialog> in every component and juggling open/close
// state plus onConfirm/onCancel callbacks, you render ONE provider near the
// top of your tree and call a promise-based `confirm()` function anywhere:
//
//     const confirm = useConfirm();
//     const ok = await confirm({ title: "Delete?", message: "..." });
//     if (!ok) return;
//
// The `await` pauses until the user clicks a button, so the "decide" and the
// "act" parts of your logic stay together and read top to bottom.
// ---------------------------------------------------------------------------
 
import { createContext, useCallback, useContext, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from "@mui/material";
 
// Options a caller may pass for a single confirmation request. Everything is
// optional, so a bare `confirm({})` still produces a sensible default dialog.
export type ConfirmOptions = {
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  // MUI color applied to the confirm button (e.g. "error" for deletes).
  confirmColor?: "primary" | "error" | "warning" | "info" | "success";
  // When true, clicking the backdrop or pressing Escape will NOT dismiss the
  // dialog. Use this for destructive actions so they can't be closed by
  // accident — the user must make an explicit Cancel/Confirm choice.
  disableBackdropClose?: boolean;
};
 
// The context exposes exactly one thing: a function that opens the dialog and
// returns a Promise resolving to the user's choice (true = confirmed).
type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;
 
// A `null` default lets the hook detect usage outside the provider and throw.
const ConfirmContext = createContext<ConfirmFn | null>(null);
 
export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  // Whether the dialog is currently shown.
  const [open, setOpen] = useState(false);
 
  // The options for the CURRENT request. Kept (not cleared) until the next
  // request so the text doesn't blank out mid-close-animation — see handleClose.
  const [options, setOptions] = useState<ConfirmOptions>({});
 
  // We hold the active Promise's `resolve` function here so the button click
  // handlers can resolve the exact promise the caller is awaiting.
  const resolverRef = useRef<((value: boolean) => void) | null>(null);
 
  // The function consumers call (via useConfirm). Stores the options, opens
  // the dialog, and returns a promise that stays pending until a button click.
  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);
 
  // Single exit path for both buttons. Closes the dialog and resolves the
  // pending promise with the user's choice.
  const handleClose = useCallback((result: boolean) => {
    setOpen(false);                // start MUI's fade-out animation
    resolverRef.current?.(result); // resolve the awaited promise
    resolverRef.current = null;    // prevent resolving the same promise twice
    // We intentionally leave `options` in place: the dialog is still visible
    // during the close animation, so clearing the text now would make it
    // flicker empty. It gets overwritten on the next confirm() call.
  }, []);
 
  // Handler for MUI's own dismissal triggers (backdrop click / Escape key).
  // MUI passes a `reason` we can inspect to optionally block those dismissals.
  const handleMuiClose = useCallback(
    (_event: object, reason: "backdropClick" | "escapeKeyDown") => {
      // For destructive confirms, ignore accidental dismissals entirely.
      if (
        options.disableBackdropClose &&
        (reason === "backdropClick" || reason === "escapeKeyDown")
      ) {
        return;
      }
      // Otherwise, dismissing the dialog counts as a cancel.
      handleClose(false);
    },
    [options.disableBackdropClose, handleClose],
  );
 
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
 
      {/* Always mounted; `open` drives show/hide so MUI can animate it. */}
      <Dialog
        open={open}
        onClose={handleMuiClose}
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
      >
        <DialogTitle id="confirm-dialog-title">
          {options.title ?? "Are you sure?"}
        </DialogTitle>
 
        <DialogContent>
          <DialogContentText id="confirm-dialog-description">
            {options.message}
          </DialogContentText>
        </DialogContent>
 
        <DialogActions>
          <Button onClick={() => handleClose(false)}>
            {options.cancelText ?? "Cancel"}
          </Button>
          <Button
            onClick={() => handleClose(true)}
            color={options.confirmColor ?? "primary"}
            variant="contained"
            autoFocus // confirm button is focused when the dialog opens
          >
            {options.confirmText ?? "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
 
// Hook consumers use to get the confirm() function. Throws a clear error if
// used outside the provider, which catches wiring mistakes early.
export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used within a ConfirmDialogProvider");
  }
  return ctx;
}